// Reports API — типизированные обёртки над backend-эндпоинтами (Sprint 9).
// Контракт сверен с apps/api reports.controller + queries/*.query.
// Все денежные поля — в копейках (_kopeck), даты — YYYY-MM-DD.

import { api } from '@/lib/api';
import { branchQueryString, setBranchParams } from '@/lib/branch-params';

export interface ReportRange {
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
  /** Филиалы шапки ∩ локальный фильтр (план 106); пусто — все выбранные. */
  branchIds?: string[];
}

function rangeQuery(range: ReportRange): string {
  const sp = new URLSearchParams();
  sp.set('from', range.from);
  sp.set('to', range.to);
  setBranchParams(sp, range.branchIds);
  return `?${sp.toString()}`;
}

// --- Dashboard -------------------------------------------------------------

/**
 * Сводка стартового экрана. Отдельный эндпоинт под правом `reports:dashboard`:
 * раньше дашборд собирался из /reports/sales + /subscriptions + /b24/deals,
 * открытых администратору, и право прятало только пункт меню.
 */
export interface DashboardSummary {
  revenue: {
    net_kopeck: number;
    sale_count: number;
    /** «Товары» — нетто продаж товаров за период (план 154 Ф5). */
    goods_kopeck: number;
    /** «Всего с товарами» = net_kopeck + goods_kopeck. */
    total_with_goods_kopeck: number;
  };
  active_subscriptions: number;
  deals: number;
}

export function getDashboardSummary(range: ReportRange): Promise<DashboardSummary> {
  return api.get<DashboardSummary>(`/reports/dashboard${rangeQuery(range)}`);
}

// --- Sales -----------------------------------------------------------------

export interface SalesReport {
  by_payment_method: Array<{ method: string; total_kopeck: number }>;
  by_template_type: Array<{ template_type: string; total_kopeck: number }>;
  by_client_type: Array<{ client_type: string; total_kopeck: number; sale_count: number }>;
  by_manager: Array<{
    employee_id: string | null;
    first_name: string | null;
    last_name: string | null;
    total_kopeck: number;
    sale_count: number;
  }>;
  totals: {
    revenue_kopeck: number;
    refunds_kopeck: number;
    net_kopeck: number;
    sale_count: number;
    avg_ticket_kopeck: number;
    /** «Товары» — нетто продаж товаров за период (план 154 Ф5). */
    goods_kopeck: number;
    /** «Всего с товарами» = net_kopeck + goods_kopeck. */
    total_with_goods_kopeck: number;
  };
  prev_totals: {
    net_kopeck: number;
    sale_count: number;
    avg_ticket_kopeck: number;
  };
}

/** Доп. фильтры продаж: продавец (sold_by) + конкретный товар/услуга (template_id). */
export interface SalesFilters {
  employeeId?: string | undefined;
  templateId?: string | undefined;
}

function salesQuery(range: ReportRange, filters: SalesFilters): string {
  const sp = new URLSearchParams();
  sp.set('from', range.from);
  sp.set('to', range.to);
  setBranchParams(sp, range.branchIds);
  if (filters.employeeId) sp.set('employee_id', filters.employeeId);
  if (filters.templateId) sp.set('template_id', filters.templateId);
  return sp.toString();
}

export function getSalesReport(
  range: ReportRange,
  filters: SalesFilters = {},
): Promise<SalesReport> {
  return api.get<SalesReport>(`/reports/sales?${salesQuery(range, filters)}`);
}

// --- Sales detail (построчный список продаж) --------------------------------

export interface SalesDetailReport {
  sales: Array<{
    sale_id: string;
    sold_at: string;
    client_id: string | null;
    client_first_name: string | null;
    client_last_name: string | null;
    client_phone: string | null;
    subscription_id: string | null;
    product_name: string;
    template_type: string | null;
    seller_id: string | null;
    seller_first_name: string | null;
    seller_last_name: string | null;
    net_kopeck: number;
    methods: string[];
    status: string;
    branch_id: string | null;
  }>;
}

export function getSalesDetail(
  range: ReportRange,
  filters: SalesFilters = {},
): Promise<SalesDetailReport> {
  return api.get<SalesDetailReport>(`/reports/sales/detail?${salesQuery(range, filters)}`);
}

// --- Cash flow (ДДС) -------------------------------------------------------

export interface CashFlowReport {
  by_category: Array<{ label: string; direction: 'in' | 'out'; amount_kopeck: number }>;
  totals: { income_kopeck: number; expense_kopeck: number; net_kopeck: number };
}

export function getCashFlow(range: ReportRange): Promise<CashFlowReport> {
  return api.get<CashFlowReport>(`/reports/cash-flow${rangeQuery(range)}`);
}

// --- Cash flow detail (построчные движения) ---------------------------------

export interface CashFlowDetailReport {
  entries: Array<{
    occurred_at: string;
    direction: 'in' | 'out';
    // Вид операции журнала (план 150 Ф3, доработка ДДС) или ручное движение кассы.
    kind:
      | 'sale'
      | 'sale_void'
      | 'payment_method_change'
      | 'debt_repayment'
      | 'deposit_topup'
      | 'deposit_topup_correction'
      | 'refund'
      | 'deposit_payout'
      | 'goods_sale'
      | 'other'
      | 'cash_in'
      | 'cash_out'
      | 'supplier_out'
      | 'supplier_in';
    label: string;
    method: string | null;
    comment: string | null;
    reference_id: string | null;
    counterparty: string | null;
    amount_kopeck: number;
    branch_id: string | null;
    /** Продавец (kind='sale'); у движений кассы и возвратов — null. */
    sold_by_name: string | null;
  }>;
  cash_summary: {
    opening_kopeck: number;
    cash_in_kopeck: number;
    cash_out_kopeck: number;
    closing_kopeck: number;
  };
}

export function getCashFlowDetail(range: ReportRange): Promise<CashFlowDetailReport> {
  return api.get<CashFlowDetailReport>(`/reports/cash-flow/detail${rangeQuery(range)}`);
}

// --- Client visits (история записей) ----------------------------------------

export interface ClientVisitsReport {
  visits: Array<{
    attendance_id: string;
    occurrence_id: string;
    scheduled_at: string;
    booked_at: string;
    client_id: string;
    client_first_name: string;
    client_last_name: string;
    client_phone: string | null;
    client_status: string;
    group_name: string | null;
    status: string;
    marked_at: string | null;
    branch_id: string | null;
    branch_name: string | null;
    subscription_label: string | null;
    subscription_id: string | null;
    source_label: string;
    /** План 153: статус занятия — у прогула на несостоявшемся своя приписка. */
    occurrence_status?: string;
    not_held_reason?: string | null;
  }>;
}

export interface ClientVisitsFilters {
  status?: string | undefined;
  clientId?: string | undefined;
}

export function getClientVisits(
  range: ReportRange,
  filters: ClientVisitsFilters = {},
): Promise<ClientVisitsReport> {
  const sp = new URLSearchParams();
  sp.set('from', range.from);
  sp.set('to', range.to);
  setBranchParams(sp, range.branchIds);
  if (filters.status) sp.set('status', filters.status);
  if (filters.clientId) sp.set('client_id', filters.clientId);
  return api.get<ClientVisitsReport>(`/reports/client-visits?${sp.toString()}`);
}

// --- Subscriptions worklist + outreach --------------------------------------

export type OutreachOutcome =
  | 'reached'
  | 'no_answer'
  | 'renewed'
  | 'refused'
  | 'callback'
  | 'other';

export interface WorklistReport {
  items: Array<{
    subscription_id: string;
    kind: 'expiring' | 'ended';
    client_id: string;
    client_first_name: string;
    client_last_name: string;
    client_phone: string | null;
    client_birth_date: string | null;
    client_status: string;
    template_name: string;
    status: string;
    expires_at: string | null;
    days_left: number | null;
    sessions_remaining: number | null;
    last_expired_end_date: string | null;
    branch_id: string | null;
    last_outcome: OutreachOutcome | null;
    last_result_name: string | null;
    last_contacted_at: string | null;
    outreach_count: number;
    open_task: { id: string; title: string; due_date: string | null } | null;
    /** «Фамилия Имя» продавца абонемента (sales.sold_by). null у импорта. */
    sold_by_name: string | null;
  }>;
}

export type WorklistItem = WorklistReport['items'][number];

export interface WorklistFilters {
  branchIds?: string[] | undefined;
  horizonDays?: number | undefined;
  endedDays?: number | undefined;
  clientStatus?: string | undefined;
  q?: string | undefined;
}

export function getSubscriptionsWorklist(filters: WorklistFilters = {}): Promise<WorklistReport> {
  const sp = new URLSearchParams();
  setBranchParams(sp, filters.branchIds);
  if (filters.horizonDays) sp.set('horizon_days', String(filters.horizonDays));
  if (filters.endedDays) sp.set('ended_days', String(filters.endedDays));
  if (filters.clientStatus) sp.set('client_status', filters.clientStatus);
  if (filters.q) sp.set('q', filters.q);
  const qs = sp.toString();
  return api.get<WorklistReport>(`/reports/subscriptions/worklist${qs ? `?${qs}` : ''}`);
}

// --- LTV report -------------------------------------------------------------

export interface LtvReport {
  items: Array<{
    client_id: string;
    client_first_name: string;
    client_last_name: string;
    client_phone: string | null;
    client_status: string;
    ltv_kopecks: number;
    first_paid_at: string | null;
    registered_at: string | null;
    last_visit_at: string | null;
    active_subscription_name: string | null;
  }>;
}

export type LtvItem = LtvReport['items'][number];

export function getLtvReport(
  filters: { q?: string | undefined; branchIds?: string[] | undefined } = {},
): Promise<LtvReport> {
  const sp = new URLSearchParams();
  if (filters.q) sp.set('q', filters.q);
  setBranchParams(sp, filters.branchIds);
  const qs = sp.toString();
  return api.get<LtvReport>(`/reports/ltv${qs ? `?${qs}` : ''}`);
}

export interface OutreachRecord {
  id: string;
  subscriptionId: string;
  clientId: string;
  employeeId: string | null;
  employeeName: string | null;
  outcome: OutreachOutcome;
  resultId: string | null;
  resultName: string | null;
  comment: string | null;
  contactedAt: string;
  createdTaskId?: string | null;
}

export interface CreateOutreachInput {
  outcome?: OutreachOutcome;
  resultId?: string;
  comment?: string;
  taskDueDate?: string; // YYYY-MM-DD — переопределение срока авто-задачи
}

export function getOutreach(subscriptionId: string): Promise<OutreachRecord[]> {
  return api.get<OutreachRecord[]>(`/reports/subscriptions/${subscriptionId}/outreach`);
}

export function createOutreach(
  subscriptionId: string,
  input: CreateOutreachInput,
): Promise<OutreachRecord> {
  const body: Record<string, unknown> = {};
  if (input.outcome) body['outcome'] = input.outcome;
  if (input.resultId) body['result_id'] = input.resultId;
  if (input.comment) body['comment'] = input.comment;
  if (input.taskDueDate) body['task_due_date'] = input.taskDueDate;
  return api.post<OutreachRecord>(`/reports/subscriptions/${subscriptionId}/outreach`, body);
}

// --- Справочник результатов обзвона -----------------------------------------

export type OutreachResultScope = 'subscription' | 'trial' | 'both';

export interface OutreachResult {
  id: string;
  name: string;
  taskTitle: string | null;
  dueOffsetDays: number | null;
  sortOrder: number;
  isArchived: boolean;
  /** План 141: для какого обзвона результат; куда он двигает сделку. */
  scope: OutreachResultScope;
  dealStageId: string | null;
  stageReasonId: string | null;
  countsNoAnswer: boolean;
}

export interface ListOutreachResultsOptions {
  includeArchived?: boolean;
  /** Список нужного обзвона: свои результаты + общие ('both'). */
  scope?: 'subscription' | 'trial';
}

export function listOutreachResults(
  options: boolean | ListOutreachResultsOptions = false,
): Promise<OutreachResult[]> {
  const opts = typeof options === 'boolean' ? { includeArchived: options } : options;
  const qs = new URLSearchParams();
  if (opts.includeArchived) qs.set('include_archived', 'true');
  if (opts.scope) qs.set('scope', opts.scope);
  const suffix = qs.size > 0 ? `?${qs.toString()}` : '';
  return api.get<{ items: OutreachResult[] }>(`/outreach-results${suffix}`).then((r) => r.items);
}

export interface OutreachResultInput {
  name: string;
  taskTitle?: string | null;
  dueOffsetDays?: number | null;
  sortOrder?: number;
  isArchived?: boolean;
}

function outreachResultBody(input: OutreachResultInput): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body['name'] = input.name;
  if (input.taskTitle !== undefined) body['task_title'] = input.taskTitle;
  if (input.dueOffsetDays !== undefined) body['due_offset_days'] = input.dueOffsetDays;
  if (input.sortOrder !== undefined) body['sort_order'] = input.sortOrder;
  if (input.isArchived !== undefined) body['is_archived'] = input.isArchived;
  return body;
}

export function createOutreachResult(
  input: OutreachResultInput,
): Promise<{ result: OutreachResult }> {
  return api.post<{ result: OutreachResult }>('/outreach-results', outreachResultBody(input));
}

export function updateOutreachResult(
  id: string,
  input: OutreachResultInput,
): Promise<{ result: OutreachResult }> {
  return api.patch<{ result: OutreachResult }>(
    `/outreach-results/${id}`,
    outreachResultBody(input),
  );
}

export function archiveOutreachResult(id: string): Promise<void> {
  return api.delete<void>(`/outreach-results/${id}`);
}

// --- Attendance ------------------------------------------------------------

export interface AttendanceReport {
  totals: {
    classes_held: number;
    attendances: number;
    no_shows: number;
    capacity_total: number;
    utilization_pct: number;
  };
  by_group: Array<{
    group_id: string;
    group_name: string;
    classes_held: number;
    attendances: number;
    capacity_total: number;
    utilization_pct: number;
  }>;
}

export function getAttendanceReport(range: ReportRange): Promise<AttendanceReport> {
  return api.get<AttendanceReport>(`/reports/attendance${rangeQuery(range)}`);
}

// --- Debts -----------------------------------------------------------------

export interface DebtsReport {
  debts: Array<{
    sale_id: string;
    client_id: string;
    client_first_name: string;
    client_last_name: string;
    client_phone: string | null;
    branch_id: string;
    sold_at: string;
    debt_amount_kopeck: number;
  }>;
  /**
   * Итог по ВСЕЙ школе (в рамках фильтра), а не по пришедшим строкам: сервер
   * считает его агрегатом без лимита (план 126, гоча 7). Складывать `debts`
   * нельзя — список режется пятьюстами строками.
   */
  total_debt_kopecks: number;
  /** Число должников-ЛЮДЕЙ, тоже без учёта лимита выдачи. */
  debtors_count: number;
}

/** Долг одного клиента в батч-ответе `by-clients`. */
export interface ClientDebtTotal {
  client_id: string;
  total_debt_kopecks: number;
}

/** Потолок выдачи сервера (reports.dto.ts). Просим максимум — см. ниже. */
export const DEBTS_LIMIT = 500;

/**
 * Долги школы.
 *
 * ЛИМИТ ПРОСИМ ЯВНО. Без параметра сервер отдаёт первые СТО строк, а виджет
 * дашборда и экран «Долги» суммируют то, что пришло, — то есть при сотне с
 * лишним долгов школа увидела бы сумму меньше настоящей и не узнала бы об
 * этом. Пятьсот — потолок схемы; на этом рубеже экран честно предупреждает,
 * что список неполный.
 */
export function getDebts(branchIds?: string[]): Promise<DebtsReport> {
  const sp = new URLSearchParams();
  setBranchParams(sp, branchIds);
  sp.set('limit', String(DEBTS_LIMIT));
  return api.get<DebtsReport>(`/reports/debts?${sp.toString()}`);
}

/**
 * Долги сразу по нескольким клиентам — один запрос на список (карточка
 * занятия, план 126 §2.4).
 *
 * Список id уходит ОДНИМ параметром через запятую: повторённый `client_id` в
 * связке Nest + zod приезжает строкой при одном значении и массивом при двух,
 * поэтому в этом API массивы всегда CSV (как `branch_ids`). В ответе только
 * клиенты с положительным долгом — отсутствие означает «долга нет».
 */
export function getDebtTotalsByClients(clientIds: string[]): Promise<{
  totals: ClientDebtTotal[];
}> {
  const sp = new URLSearchParams();
  sp.set('client_ids', clientIds.join(','));
  return api.get<{ totals: ClientDebtTotal[] }>(`/reports/debts/by-clients?${sp.toString()}`);
}

// --- Expiring --------------------------------------------------------------

export interface ExpiringReport {
  subscriptions: Array<{
    subscription_id: string;
    client_id: string;
    client_first_name: string;
    client_last_name: string;
    client_phone: string | null;
    template_name: string;
    expires_at: string;
    sessions_remaining: number | null;
    branch_id: string;
  }>;
}

export function getExpiring(branchIds?: string[]): Promise<ExpiringReport> {
  const sp = new URLSearchParams();
  setBranchParams(sp, branchIds);
  const qs = sp.toString();
  return api.get<ExpiringReport>(`/reports/expiring${qs ? `?${qs}` : ''}`);
}

// --- Churn -----------------------------------------------------------------

export interface ChurnReport {
  active_at_start: number;
  lost_in_period: number;
  churn_pct: number;
}

export function getChurn(range: ReportRange): Promise<ChurnReport> {
  return api.get<ChurnReport>(`/reports/churn${rangeQuery(range)}`);
}

// --- Teachers --------------------------------------------------------------

export interface TeachersReport {
  teachers: Array<{
    teacher_id: string;
    first_name: string;
    last_name: string;
    classes_held: number;
    attendances: number;
    no_shows: number;
    avg_attendance_per_class: number;
  }>;
}

export function getTeachers(range: ReportRange): Promise<TeachersReport> {
  return api.get<TeachersReport>(`/reports/teachers${rangeQuery(range)}`);
}

// --- Дашборд по филиалам (§19): CRM-метрики + ручной маркетинг ---------------

export interface MarketingBranchRow {
  branch_id: string;
  branch_name: string;
  revenue_kopecks: number;
  revenue_prev_kopecks: number;
  revenue_year_kopecks: number;
  visits: number;
  visits_prev: number;
  visits_year: number;
  avg_check_kopecks: number;
  visits_per_day: number;
  budget_kopecks: number;
  leads: number;
  revenue_plan_kopecks: number;
  cpl_kopecks: number | null;
  roi_pct: number | null;
  plan_pct: number | null;
}

export interface MarketingDashboard {
  year: number;
  month: number;
  days_in_month: number;
  branches: MarketingBranchRow[];
}

export function getMarketingDashboard(year: number, month: number): Promise<MarketingDashboard> {
  return api.get<MarketingDashboard>(`/reports/marketing-dashboard?year=${year}&month=${month}`);
}

export interface MarketingInputsBody {
  branch_id: string;
  year: number;
  month: number;
  budget_kopecks: number;
  leads: number;
  revenue_plan_kopecks: number;
}

export function saveMarketingInputs(body: MarketingInputsBody): Promise<{ ok: true }> {
  return api.put<{ ok: true }>('/reports/marketing-inputs', body);
}

// --- Д12 «Использование приложения» + Д13 «Активная база» (батч №18) ---------

export interface AppUsageReport {
  active_narrow: number;
  active_wide: number;
  registered: number;
  registered_active: number;
  /** Разбивка активаций по площадкам (батч №23 П12). */
  registered_telegram: number;
  registered_max: number;
  registered_both: number;
  using_14d: number;
  using_active: number;
}

export function getAppUsageReport(branchIds?: string[]): Promise<AppUsageReport> {
  return api.get<AppUsageReport>(`/reports/app-usage${branchQueryString(branchIds)}`);
}

export interface ActiveBaseReport {
  months: Array<{ month: string; active: number }>;
}

export function getActiveBaseReport(branchIds?: string[]): Promise<ActiveBaseReport> {
  return api.get<ActiveBaseReport>(`/reports/active-base${branchQueryString(branchIds)}`);
}

// --- Отчёт «Товары» (план 154 Ф4, §2.8) ---------------------------------------

export interface GoodsReportItemTotals {
  category_id: string;
  category_name: string;
  cash_qty: number;
  cash_kopecks: number;
  cashless_qty: number;
  cashless_kopecks: number;
  revenue_kopecks: number;
  cost_kopecks: number;
  markup_kopecks: number;
  markup_percent: number | null;
  margin_percent: number | null;
  promo_discount_kopecks: number;
}

export interface GoodsReportItemRow extends GoodsReportItemTotals {
  product_id: string;
  product_name: string;
}

export interface GoodsReport {
  period: { from: string; to: string };
  items: {
    rows: GoodsReportItemRow[];
    categories: GoodsReportItemTotals[];
    totals: GoodsReportItemTotals;
  };
  drinks: {
    rows: Array<{
      product_id: string;
      product_name: string;
      category_name: string;
      cash_qty: number;
      cash_kopecks: number;
      cashless_qty: number;
      cashless_kopecks: number;
      revenue_kopecks: number;
    }>;
    revenue_kopecks: number;
    supplies_kopecks: number;
    gifts_cost_kopecks: number;
  };
  gifts: {
    rows: Array<{
      drink_product_id: string;
      drink_name: string;
      gift_product_id: string;
      gift_name: string;
      given_qty: number;
      given_cost_kopecks: number;
      short_qty: number;
      declined_qty: number;
    }>;
    given_qty: number;
    cost_kopecks: number;
  };
  stock: {
    rows: Array<{
      branch_id: string;
      branch_name: string;
      product_id: string;
      product_name: string;
      category_name: string;
      qty: number;
      avg_cost: string;
      value_kopecks: number;
    }>;
    value_kopecks: number;
  };
  receipts: Array<{
    supplier_id: string;
    supplier_name: string;
    receipts_count: number;
    payable_kopecks: number;
    paid_cash_desk_kopecks: number;
    paid_outside_kopecks: number;
    debt_kopecks: number;
  }>;
  debts: Array<{
    receipt_id: string;
    supplier_name: string;
    branch_name: string;
    received_on: string;
    invoice_no: string | null;
    debt_kopecks: number;
    days_waiting: number;
  }>;
  corrections: Array<{
    correction_id: string;
    receipt_id: string;
    received_on: string;
    supplier_name: string | null;
    product_name: string;
    qty_delta: number | null;
    amount_delta_kopecks: number;
    value_delta_kopecks: number | null;
    gone_kopecks: number | null;
    comment: string;
    created_by_name: string;
    created_at: string;
  }>;
  openings: Array<{
    receipt_id: string;
    received_on: string;
    branch_name: string;
    product_name: string;
    qty: number;
    unit_cost_kopecks: number;
    amount_kopecks: number;
    comment: string | null;
    created_by_name: string;
    created_at: string;
  }>;
  write_offs: Array<{
    reason: 'defect' | 'spoilage' | 'theft' | 'internal_use' | 'other';
    qty: number;
    cost_kopecks: number;
  }>;
}

export interface GoodsReportFilters {
  categoryId?: string;
  productId?: string;
}

export function getGoodsReport(range: ReportRange, f: GoodsReportFilters): Promise<GoodsReport> {
  const sp = new URLSearchParams();
  sp.set('from', range.from);
  sp.set('to', range.to);
  setBranchParams(sp, range.branchIds);
  if (f.categoryId) sp.set('category_id', f.categoryId);
  if (f.productId) sp.set('product_id', f.productId);
  return api.get<GoodsReport>(`/reports/goods?${sp.toString()}`);
}
